/* eslint-disable jsx-a11y/anchor-is-valid */
import classNames from 'classnames';
import {
    useCallback, useEffect, useRef, useState
} from 'react';
import { Button } from 'palette/Button';
import { FilterDropdownRedesign } from '@common/Filters/components';
import BrandFilterDropdown from '@common/Filters/containers/BrandFilterDropdown';
import GenderFilter from '@common/Filters/containers/GenderFilter';
import ScrollFilterDropdown from '@common/Filters/containers/ScrollFilterDropdown';
import { getDropdownIcon } from '@common/Filters/helpers/filterItemHelpers';
import { getWrapperClassName } from '@common/Filters/helpers/filtersItem';
import { useBrandsGroup } from '@common/Filters/hooks/useBrandGroups';
import OutsideClick from '@common/OutsideClick';
import SortBy from '@common/SortBy/containers/SortBy';
import { FILTER_TYPES } from '../constants/filterTypes';
import '../styles/FiltersListItem.scss';

export function FiltersListItem({
    activeFilter,
    filter,
    theme,
    isMultiColumnSizeFilter,
    onClick,
    onFilterCloseClick
}) {
    const containerRef = useRef(null);
    const [rightMargin, setRightMargin] = useState();
    const { title, type } = filter;
    const { brandGroupsData, activeBrandIds } = useBrandsGroup(type === FILTER_TYPES.brand);
    const activeFilterType = activeFilter?.type;
    const isExpanded = activeFilterType === type;
    const dropdownIcon = getDropdownIcon(type, theme, isExpanded);

    const getContent = useCallback(() => {
        if (!isExpanded) {
            return null;
        }

        if (activeFilterType === FILTER_TYPES.brand) {
            return (
                <BrandFilterDropdown
                    activeBrandIds={activeBrandIds}
                    brandGroupsData={brandGroupsData}
                    onCloseClick={onFilterCloseClick}
                />
            );
        }

        if (activeFilterType === FILTER_TYPES.productSeries) {
            return <ScrollFilterDropdown filterType={FILTER_TYPES.productSeries} />;
        }

        if (activeFilterType === FILTER_TYPES.sortBy) {
            return <SortBy topLabel={title} />;
        }

        return (
            <FilterDropdownRedesign
                filterType={activeFilterType}
                hasMultipleGroups={isMultiColumnSizeFilter && activeFilterType === FILTER_TYPES.size}
                isTranslatable={activeFilterType !== FILTER_TYPES.material && activeFilterType !== FILTER_TYPES.pattern}
                title={title}
            />
        );
    }, [
        brandGroupsData,
        activeBrandIds,
        activeFilterType,
        isExpanded,
        isMultiColumnSizeFilter,
        title,
        onFilterCloseClick
    ]);

    useEffect(() => {
        setRightMargin(window.innerWidth - (containerRef.current.getBoundingClientRect().right));

        window.addEventListener('resize', () => {
            setRightMargin(window.innerWidth - (containerRef.current.getBoundingClientRect().right));
        });

        return window.removeEventListener('resize', () => {
            setRightMargin(window.innerWidth - (containerRef.current.getBoundingClientRect().right));
        });
    }, []);

    useEffect(() => {
        let listener;

        if (isExpanded) {
            listener = new OutsideClick(containerRef.current, onFilterCloseClick);
            listener.register();
        }

        return () => {
            if (listener) {
                listener.unregister();
            }
        };
    }, [isExpanded, onFilterCloseClick]);

    const dropdownLeft = rightMargin < 300;

    return (
        <li
            ref={containerRef}
            className={classNames('filters-list-item', {
                'filters-list-item--active': isExpanded,
                [`filters-list-item--${theme}`]: theme
            })}
        >
            {type === 'gender'
                ? (<GenderFilter devicetype="desktop" />)
                : (
                    <Button
                        iconName={dropdownIcon}
                        label={title}
                        size="medium"
                        testId={`listingFilters-${type}`}
                        theme="secondary"
                        onClick={onClick}
                    />
                )}
            <nav
                aria-label="sorting navigation"
                className={getWrapperClassName({
                    isExpanded,
                    dropdownLeft
                })}
            >
                {getContent()}
            </nav>
        </li>
    );
}
